// Language-aware voice intent helpers for Undo/Redo
// Normalizes text and matches per-language synonyms

export const normalizeForMatching = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim();

const spacey = (w) =>
  String(w || "")
    .toLowerCase()
    .replace(/\s+/g, "\\s+");
const buildRegex = (words) => {
  const alt = words.map(spacey).join("|");
  return new RegExp(`\\b(${alt})\\b`, "i");
};

// Map base language code -> synonyms
const SYNONYMS = {
  en: {
    undo: [
      "undo",
      "roll back",
      "rolled back",
      "rollback",
      "revert",
      "reverted",
      "reverting",
      "reverse",
    ],
    redo: [
      "redo",
      "re do",
      "reapply",
      "apply again",
      "do again",
      "repeat last action",
      "repeat last command",
      "repeat last change",
    ],
    clean: ["clean", "reset", "start over", "begin again", "refresh"],
    confirm: [
      "go now",
      "execute",
      "confirm",
      "proceed",
      "go ahead",
      "do it",
      "run",
      "apply",
      "submit",
      "okay",
      "ok",
    ],
  },
  es: {
    undo: [
      "deshacer",
      "deshaz",
      "deshace",
      "revertir",
      "revierte",
      "revierta",
      "retroceder",
      "marcha atras",
      "dar marcha atras",
      "rollback",
      "roll back",
      "rolled back",
    ],
    redo: [
      "rehacer",
      "hacer de nuevo",
      "reaplicar",
      "volver a aplicar",
      "aplicar de nuevo",
      "rehacer cambio",
      "reaplicar cambio",
    ],
    clean: [
      "limpiar",
      "reiniciar",
      "empezar de nuevo",
      "resetear",
      "refrescar",
      "clean",
    ],
    confirm: [
      "adelante",
      "ejecutar",
      "confirmar",
      "proceder",
      "continuar",
      "hacerlo",
      "aplicar",
      "enviar",
      "okay",
      "ok",
      "go now",
    ],
  },
  pt: {
    undo: [
      "desfazer",
      "reverter",
      "voltar",
      "voltar atras",
      "rollback",
      "roll back",
      "rolled back",
    ],
    redo: [
      "refazer",
      "fazer novamente",
      "reaplicar",
      "aplicar novamente",
      "refazer alteração",
      "reaplicar alteração",
    ],
    clean: [
      "limpar",
      "reiniciar",
      "começar de novo",
      "resetar",
      "atualizar",
      "clean",
    ],
    confirm: [
      "vamos lá",
      "executar",
      "confirmar",
      "prosseguir",
      "continuar",
      "fazer",
      "aplicar",
      "enviar",
      "okay",
      "ok",
      "go now",
    ],
  },
  fr: {
    undo: [
      "annuler",
      "revenir",
      "retour en arriere",
      "revenir en arriere",
      "rollback",
      "roll back",
      "révoquer",
    ],
    redo: [
      "refaire",
      "réappliquer",
      "reappliquer",
      "appliquer à nouveau",
      "faire à nouveau",
    ],
    clean: ["nettoyer", "réinitialiser", "recommencer", "rafraîchir", "clean"],
    confirm: [
      "aller",
      "exécuter",
      "confirmer",
      "procéder",
      "continuer",
      "faire",
      "appliquer",
      "soumettre",
      "d'accord",
      "okay",
      "ok",
      "go now",
    ],
  },
  de: {
    undo: [
      "rückgängig",
      "rückgängig machen",
      "zurücksetzen",
      "zurück",
      "rollback",
      "roll back",
      "widerrufen",
    ],
    redo: [
      "wiederholen der letzten aktion",
      "letzte aktion wiederholen",
      "erneut anwenden",
      "nochmals anwenden",
      "erneut durchführen",
    ],
    clean: [
      "bereinigen",
      "zurücksetzen",
      "neu starten",
      "aktualisieren",
      "clean",
    ],
    confirm: [
      "los",
      "ausführen",
      "bestätigen",
      "fortfahren",
      "weiter",
      "machen",
      "anwenden",
      "einreichen",
      "okay",
      "ok",
      "go now",
    ],
  },
  hi: {
    undo: [
      "पूर्ववत",
      "पूर्ववत करें",
      "वापस लें",
      "उन्डू",
      "रोलबैक",
      "rollback",
      "roll back",
    ],
    redo: [
      "पुनः करें",
      "फिर से करें",
      "रीडो",
      "पुनः लागू करें",
      "दोबारा लागू करें",
    ],
    clean: [
      "साफ करें",
      "रीसेट करें",
      "फिर से शुरू करें",
      "ताज़ा करें",
      "clean",
    ],
    confirm: [
      "जाओ",
      "निष्पादित करें",
      "पुष्टि करें",
      "आगे बढ़ें",
      "जारी रखें",
      "करो",
      "लागू करें",
      "जमा करें",
      "ठीक",
      "okay",
      "ok",
      "go now",
    ],
  },
  it: {
    undo: [
      "annulla",
      "annullare",
      "ripristina",
      "ripristinare",
      "rollback",
      "roll back",
      "reverti",
    ],
    redo: ["rifare", "riapplicare", "applicare di nuovo", "fare di nuovo"],
    clean: ["pulire", "reimpostare", "ricominciare", "aggiornare", "clean"],
    confirm: [
      "vai",
      "esegui",
      "conferma",
      "procedi",
      "continua",
      "fallo",
      "applica",
      "invia",
      "okay",
      "ok",
      "go now",
    ],
  },
  ja: {
    undo: ["取り消し", "元に戻す", "ロールバック", "rollback", "roll back"],
    redo: ["やり直す", "再適用", "再実行", "もう一度実行"],
    clean: ["クリーン", "リセット", "最初からやり直す", "更新", "clean"],
    confirm: [
      "実行",
      "確認",
      "進める",
      "続ける",
      "実行する",
      "適用",
      "送信",
      "okay",
      "ok",
      "go now",
    ],
  },
};

export const getLanguageCode = (i18nLang) => {
  // Normalize to base code, e.g., en-US -> en
  const base = String(i18nLang || "en")
    .toLowerCase()
    .split("-")[0];
  return SYNONYMS[base] ? base : "en";
};

// Map the current i18n language to the closest browser SpeechRecognition code
// Centralized so both ACV and embedded Whiteboard keep the same language rules.
export const getSpeechRecognitionLanguage = (i18nLang) => {
  const base = getLanguageCode(i18nLang);
  const map = {
    en: "en-US",
    es: "es-ES",
    pt: "pt-PT",
    fr: "fr-FR",
    de: "de-DE",
    hi: "hi-IN",
    it: "it-IT",
    ja: "ja-JP",
  };
  return map[base] || "en-US";
};

// Detect a secondary wake-word prefix for platform/module navigation.
// Supports English and Spanish terms: "platform", "tools", "plataforma", "herramienta".
// Returns true when the text contains one of these keywords near the beginning.
export const hasPlatformWakePrefix = (text) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const platformWords = [
    "platform",
    "tools",
    "plataforma",
    "herramienta",
    "herramientas",
  ];
  const re = new RegExp(`^(?:hello\s+)?(?:ines\s+)?(?:${platformWords
    .map((w) => w.replace(/\s+/g, "\\s+"))
    .join("|")})(?:\s*:\s*|\s+)`);
  return re.test(s);
};

// Multilingual synonyms for the noun "attendance"
const ATTENDANCE_NOUNS_GENERIC = [
  // English
  "attendance",
  "attendance report",
  // Spanish
  "asistencia",
  "reporte de asistencia",
  "informe de asistencia",
  // Portuguese
  "assiduidade",
  "relatorio de assiduidade",
  "relatório de assiduidade",
  "frequencia",
  "frequência",
  "presenca",
  "presença",
  "relatorio de presenca",
  "relatório de presença",
  // French
  "presence",
  "présence",
  "rapport de presence",
  "rapport de présence",
  "feuille de presence",
  "feuille de présence",
  "liste de presence",
  "liste de présence",
  "registre de presence",
  "registre de présence",
  // German
  "anwesenheit",
  "anwesenheitsbericht",
  "anwesenheitsliste",
  "präsenz",
  "praesenz",
  "präsenzbericht",
  "praesenzbericht",
  "präsenzliste",
  "praesenzliste",
  // Italian
  "presenza",
  "rapporto di presenza",
  "rapporto presenze",
  "registro presenze",
  "foglio presenze",
  // Hindi
  "उपस्थिति",
  "उपस्थिति रिपोर्ट",
  "हाजिरी",
  "हाज़री",
  // Japanese
  "出席",
  "出席レポート",
  "出席報告",
  "出席報告書",
];

// Generic export verbs from various languages (reuse existing list if available)
const EXPORT_VERBS_ALT = [
  "export",
  "download",
  "save",
  // Spanish
  "exportar",
  "exporte",
  "exporta",
  "descargar",
  "descargue",
  "descarga",
  "guardar",
  "guarde",
  // Portuguese
  "exportar",
  "baixar",
  "salvar",
  // French
  "exporter",
  "telecharger",
  "télécharger",
  "enregistrer",
  "exporte",
  "exportez",
  "telecharge",
  "télécharge",
  "telechargez",
  "téléchargez",
  "enregistre",
  "enregistrez",
  // German
  "exportieren",
  "herunterladen",
  "speichern",
  "exportiere",
  "exportiert",
  "lade herunter",
  "downloaden",
  "speichere",
  // Italian
  "esportare",
  "scaricare",
  "salvare",
  "esporta",
  "scarica",
  "salva",
  // Hindi
  "निर्यात",
  "डाउनलोड",
  "डाउनलोड करें",
  "सहेजें",
  "सहेजना",
  // Japanese
  "エクスポート",
  "ダウンロード",
  "保存",
  "出力",
  // Japanese with verb helper "する"
  "エクスポートする",
  "ダウンロードする",
  "保存する",
  "出力する",
];

// Check if text indicates an intent to export attendance data
export const isExportAttendanceIntent = (text) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  // For CJK scripts, ASCII word boundaries (\b) don't work. Relax boundaries.
  const hasCJK = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff]/.test(s);
  const verbsAlt = EXPORT_VERBS_ALT.map((w) => w.replace(/\s+/g, "\\s+")).join("|");
  const nounsAlt = ATTENDANCE_NOUNS_GENERIC.map((w) => w.replace(/\s+/g, "\\s+")).join("|");
  const verbRe = hasCJK
    ? new RegExp(`(${verbsAlt})`, "i")
    : new RegExp(`\\b(${verbsAlt})\\b`, "i");
  const nounRe = hasCJK
    ? new RegExp(`(${nounsAlt})`, "i")
    : new RegExp(`\\b(${nounsAlt})\\b`, "i");
  return verbRe.test(s) && nounRe.test(s);
};

export const isUndoIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const synonyms = SYNONYMS[getLanguageCode(langCode)].undo;
  const re = buildRegex(synonyms);
  if (re.test(normalized)) return true;

  // Special English flexible pattern for roll\s*back / rolled\s*back
  if (
    /\broll\s*back\b/i.test(normalized) ||
    /\brolled\s*back\b/i.test(normalized)
  ) {
    return true;
  }

  return false;
};

export const isRedoIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const synonyms = SYNONYMS[getLanguageCode(langCode)].redo || [];
  if (synonyms.length) {
    const re = buildRegex(synonyms);
    if (re.test(normalized)) return true;
  }

  // Special English flexible pattern for re[-\s]*do
  if (/\bre\s*do\b/i.test(normalized)) {
    return true;
  }

  return false;
};

export const isCleanIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const synonyms = SYNONYMS[getLanguageCode(langCode)].clean || [];
  if (synonyms.length) {
    const re = buildRegex(synonyms);
    if (re.test(normalized)) return true;
  }

  return false;
};

export const isConfirmIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const synonyms = SYNONYMS[getLanguageCode(langCode)].confirm || [];
  if (synonyms.length) {
    const re = buildRegex(synonyms);
    if (re.test(normalized)) return true;
  }

  // Special English flexible pattern for go\s+now / go\s+ahead
  if (/\b(go\s+(now|ahead)|do\s+it)\b/i.test(normalized)) {
    return true;
  }

  return false;
};

export const isCancelIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  // Reuse generic close/dismiss/cancel verbs
  const re = buildRegex(CLOSE_VERBS_GENERIC);
  if (re.test(normalized)) return true;
  // Common short negatives that users say at confirmation
  if (/\b(no|not\s+now|nope)\b/i.test(normalized)) return true;
  return false;
};

// Library dialog multilingual intent synonyms (open/close)
const LIBRARY_SYNONYMS = {
  en: {
    open: [
      "open library",
      "show library",
      "open the library",
      // Do not treat collections phrases as generic open; handled by top-tab intent
    ],
    close: [
      "close library",
      "hide library",
      "close collections",
      "hide collections",
      "close the library",
      "close the collections",
    ],
  },
  es: {
    open: [
      "abrir biblioteca",
      "mostrar biblioteca",
      // Collections phrases handled by top-tab intent
    ],
    close: [
      "cerrar biblioteca",
      "ocultar biblioteca",
      "cerrar colecciones",
      "ocultar colecciones",
      "cierra la biblioteca",
      "cierre la biblioteca",
      "oculta la biblioteca",
      "cierra la coleccion",
      "cierra la colección",
      "cierre la coleccion",
      "cierre la colección",
      "oculta la coleccion",
      "oculta la colección",
      "cerrar el dialogo de la biblioteca",
      "cerrar el diálogo de la biblioteca",
      "cierra el dialogo de la biblioteca",
      "cierra el diálogo de la biblioteca",
      "cierre el dialogo de la biblioteca",
      "cierre el diálogo de la biblioteca",
    ],
  },
  pt: {
    open: [
      "abrir biblioteca",
      "mostrar biblioteca",
      // Collections phrases handled by top-tab intent
    ],
    close: [
      "fechar biblioteca",
      "ocultar biblioteca",
      "fechar colecoes",
      "ocultar colecoes",
      "fechar coleções",
      "ocultar coleções",
    ],
  },
  fr: {
    open: [
      "ouvrir la bibliotheque",
      "afficher la bibliotheque",
      "ouvrir la bibliothèque",
      "afficher la bibliothèque",
    ],
    close: [
      "fermer la bibliotheque",
      "masquer la bibliotheque",
      "fermer les collections",
      "masquer les collections",
      "fermer la bibliothèque",
      "masquer la bibliothèque",
    ],
  },
  de: {
    open: [
      "bibliothek öffnen",
      "bibliothek anzeigen",
      // Collections phrases handled by top-tab intent
    ],
    close: [
      "bibliothek schließen",
      "bibliothek ausblenden",
      "sammlungen schließen",
      "sammlungen ausblenden",
    ],
  },
  hi: {
    open: [
      "लाइब्रेरी खोलें",
      "लाइब्रेरी दिखाएं",
      // Collections phrases handled by top-tab intent
    ],
    close: [
      "लाइब्रेरी बंद करें",
      "लाइब्रेरी छुपाएं",
      "संग्रह बंद करें",
      "संग्रह छुपाएं",
    ],
  },
  it: {
    open: [
      "aprire la libreria",
      "mostrare la libreria",
      "aprire le collezioni",
      "mostrare le collezioni",
    ],
    close: [
      "chiudere la libreria",
      "nascondere la libreria",
      "chiudere le collezioni",
      "nascondere le collezioni",
    ],
  },
  ja: {
    open: [
      "ライブラリを開く",
      "ライブラリを表示",
      "コレクションを開く",
      "コレクションを表示",
    ],
    close: [
      "ライブラリを閉じる",
      "ライブラリを隠す",
      "コレクションを閉じる",
      "コレクションを隠す",
    ],
  },
};

// Generic cross-language verb/noun sets for flexible intent detection
const OPEN_VERBS_GENERIC = [
  // English
  "open",
  "show",
  "display",
  "launch",
  "start",
  "bring up",
  "pop up",
  "reveal",
  // Spanish
  "abrir",
  "mostrar",
  "abre",
  "muestra",
  // Portuguese
  "abrir",
  "mostrar",
  // French
  "ouvrir",
  "afficher",
  // German
  "öffnen",
  "anzeigen",
  // Hindi
  "खोलें",
  "दिखाएं",
  // Italian
  "aprire",
  "mostrare",
  // Japanese
  "開く",
  "表示",
];

const CLOSE_VERBS_GENERIC = [
  // English
  "close",
  "hide",
  "dismiss",
  "shut",
  "remove",
  "exit",
  "cancel",
  // Spanish
  "cerrar",
  "ocultar",
  "cierra",
  "cierre",
  // Portuguese
  "fechar",
  "ocultar",
  "fecha",
  // French
  "fermer",
  "masquer",
  "ferme",
  // German
  "schließen",
  "ausblenden",
  "schließe",
  // Hindi
  "बंद",
  "छुपाएं",
  "बंद करें",
  // Italian
  "chiudere",
  "nascondere",
  "chiudi",
  // Japanese
  "閉じる",
  "隠す",
];

const LIBRARY_NOUNS_GENERIC = [
  // English
  "library",
  "collections",
  "collection",
  "assets",
  "templates",
  // Spanish
  "biblioteca",
  "colecciones",
  "coleccion",
  "colección",
  // Portuguese
  "biblioteca",
  "colecoes",
  "coleções",
  // French
  "bibliotheque",
  "bibliothèque",
  "collections",
  "collection",
  // German
  "bibliothek",
  "sammlungen",
  "sammlung",
  // Hindi
  "लाइब्रेरी",
  "संग्रह",
  // Italian
  "libreria",
  "collezioni",
  "collezione",
  // Japanese
  "ライブラリ",
  "コレクション",
];

// New: nouns that imply returning to the editor/canvas

const DIALOG_NOUNS_GENERIC = [
  // English
  "dialog",
  "menu",
  "panel",
  "window",
  // Spanish
  "dialogo",
  "menú",
  "panel",
  "ventana",
  // Portuguese
  "dialogo",
  "menu",
  "painel",
  "janela",
  // French
  "dialogue",
  "menu",
  "panneau",
  "fenetre",
  "fenêtre",
  // German
  "dialog",
  "menü",
  "panel",
  "fenster",
  // Hindi
  "संवाद",
  "मेनू",
  "पैनल",
  "विंडो",
  // Italian
  "dialogo",
  "menu",
  "pannello",
  "finestra",
  // Japanese
  "ダイアログ",
  "メニュー",
  "パネル",
  "ウィンドウ",
];

const buildAltRegex = (arr) =>
  new RegExp(`\\b(${arr.map(spacey).join("|")})\\b`, "i");

const containsVerbAndNoun = (normalized, verbs, nouns) => {
  const vRe = buildAltRegex(verbs);
  const nRe = buildAltRegex(nouns);
  const hasVerb = vRe.test(normalized);
  const hasNoun = nRe.test(normalized);
  if (hasVerb && hasNoun) return true;
  // Also allow noun + verb order with up to 6 tokens between
  const vAlt = verbs.map(spacey).join("|");
  const nAlt = nouns.map(spacey).join("|");
  const proximity = new RegExp(
    `\\b(${vAlt})\\b[\\s\\S]{0,80}\\b(${nAlt})\\b|\\b(${nAlt})\\b[\\s\\S]{0,80}\\b(${vAlt})\\b`,
    "i"
  );
  return proximity.test(normalized);
};

export const isOpenLibraryIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const base = getLanguageCode(langCode);
  const synonyms =
    (LIBRARY_SYNONYMS[base] && LIBRARY_SYNONYMS[base].open) || [];
  if (synonyms.length) {
    const re = buildRegex(synonyms);
    if (re.test(normalized)) return true;
  }
  // Fallback: generic verbs+nouns detection
  if (
    containsVerbAndNoun(normalized, OPEN_VERBS_GENERIC, LIBRARY_NOUNS_GENERIC)
  )
    return true;
  // Do NOT open on generic dialog mentions; require explicit library nouns
  return false;
};

export const isCloseLibraryIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const base = getLanguageCode(langCode);
  const synonyms =
    (LIBRARY_SYNONYMS[base] && LIBRARY_SYNONYMS[base].close) || [];
  if (synonyms.length) {
    const re = buildRegex(synonyms);
    if (re.test(normalized)) return true;
  }
  // Fallback: generic verbs+nouns detection
  if (
    containsVerbAndNoun(normalized, CLOSE_VERBS_GENERIC, LIBRARY_NOUNS_GENERIC)
  )
    return true;
  // Do NOT close on generic dialog mentions; require explicit library nouns
  return false;
};

const HELP_PHRASES_GENERIC = [
  "what can i say",
  "what can i do",
  "show commands",
  "show command list",
  "show commands list",
  "show voice commands",
  "help",
  "voice help",
  "que puedo decir",
  "qué puedo decir",
  "que puedo hacer",
  "qué puedo hacer",
  "mostrar comandos",
  "muestra comandos",
  "mostrar comandos de voz",
  "muestra comandos de voz",
  "mostrar lista de comandos",
  "muestra la lista de comandos",
  "ayuda",
  "ayuda de voz",
  "o que posso dizer",
  "o que posso fazer",
  "mostrar comandos",
  "mostrar comandos de voz",
  "mostrar lista de comandos",
  "ajuda",
  "ajuda de voz",
  "que puis je dire",
  "que puis-je dire",
  "que puis je faire",
  "que puis-je faire",
  "afficher les commandes",
  "afficher les commandes vocales",
  "afficher la liste des commandes",
  "aide",
  "aide vocale",
  "was kann ich sagen",
  "was kann ich tun",
  "was kann ich machen",
  "befehle anzeigen",
  "befehlsliste anzeigen",
  "zeige befehle",
  "sprachbefehle anzeigen",
  "hilfe",
  "main kya keh sakta hun",
  "main kya keh sakti hun",
  "मैं क्या कह सकती हूं",
  "मैं क्या कह सकता हूं",
  "कमांड दिखाएं",
  "कमांड्स दिखाएं",
  "कमांड सूची दिखाएं",
  "सहायता",
  "मदद",
  "आवाज सहायता",
  "cosa posso dire",
  "cosa posso fare",
  "mostra comandi",
  "mostra comandi vocali",
  "mostra la lista dei comandi",
  "aiuto",
  "aiuto vocale",
  "何を言えばいいでしょうか",
  "何と言えばいいですか",
  "何を言えばいいですか",
  "何ができますか",
  "何をすればいいですか",
  "コマンドを表示",
  "コマンド一覧を表示",
  "音声コマンドを表示",
  "ヘルプ",
  "音声ヘルプ",
];

export const isHelpIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasCJK = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff]/.test(s);
  const alt = HELP_PHRASES_GENERIC.map((w) => w.replace(/\s+/g, "\\s+")).join("|");
  const re = hasCJK ? new RegExp(`(${alt})`, "i") : new RegExp(`\\b(${alt})\\b`, "i");
  if (re.test(s)) return true;
  if (/\b(what\s+can\s+i\s+(say|do))\b/i.test(s)) return true;
  if (/\b(show\s+(voice\s+)?commands?)\b/i.test(s)) return true;
  if (/\b(help)\b/i.test(s)) return true;
  return false;
};

// New: detect intent like "go to editor" to leave library

// Top tab switching intent helpers
const SWITCH_VERBS_GENERIC = [
  // English
  "switch",
  "go",
  "go to",
  // Spanish
  "cambiar",
  // Portuguese
  "mudar",
  // French
  "changer",
  // German
  "wechseln",
  // Hindi
  "स्विच",
  // Italian
  "passa a",
  // Japanese
  "切り替え",
];

const TOP_TAB_MY_LIBRARY = [
  // English
  "my library",
  // Spanish
  "mi biblioteca",
  // Portuguese
  "minha biblioteca",
  // French
  "ma bibliotheque",
  "ma bibliothèque",
  // German
  "meine bibliothek",
  // Hindi
  "मेरी लाइब्रेरी",
  // Italian
  "mia libreria",
  // Japanese
  "私のライブラリ",
];

const TOP_TAB_COLLECTIONS = [
  // English
  "collections",
  // Spanish
  "colecciones",
  "coleccion",
  "colección",
  // Portuguese
  "colecoes",
  "coleções",
  // French
  "collections",
  "collection",
  // German
  "sammlungen",
  "sammlung",
  // Hindi
  "संग्रह",
  // Italian
  "collezioni",
  "collezione",
  // Japanese
  "コレクション",
];

export const getLibraryTopTabTarget = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const collectionsRe = buildAltRegex(TOP_TAB_COLLECTIONS);
  const myLibRe = buildAltRegex(TOP_TAB_MY_LIBRARY);

  // Avoid false positives: don't match "collections" if it's part of a sidebar category phrase
  const sidebarCategoryPhrases = [
    "added from collections",
    "added from collection",
    "from collections",
    "from collection",
  ];
  const isSidebarCategory = sidebarCategoryPhrases.some((phrase) =>
    normalized.includes(phrase.toLowerCase())
  );

  if (collectionsRe.test(normalized) && !isSidebarCategory)
    return "collections";
  if (myLibRe.test(normalized)) return "myLibrary";
  return null;
};

export const isSwitchLibraryTopTabIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  const hasTarget = !!getLibraryTopTabTarget(text, langCode);
  if (!hasTarget) return false;
  // Allow explicit open/show verbs OR "go to"/"switch" phrasing for tab changes
  const hasOpen = buildAltRegex(OPEN_VERBS_GENERIC).test(normalized);
  const hasSwitchWord = buildAltRegex(SWITCH_VERBS_GENERIC).test(normalized);
  const hasGoTo = /\b(go\s*to)\b/i.test(normalized);
  return hasOpen || hasSwitchWord || hasGoTo;
};
// Add generic sidebar nouns across languages to detect sidebar/category/tab references
export const SIDEBAR_NOUNS_GENERIC = [
  // English
  "sidebar",
  "side bar",
  "category",
  "categories",
  "tab",
  "pane",
  // Spanish
  "barra lateral",
  "categoria",
  "categorias",
  "pestaña",
  // Portuguese
  "barra lateral",
  "categoria",
  "categorias",
  "aba",
  // French
  "barre laterale",
  "barre latérale",
  "categorie",
  "catégorie",
  "categories",
  "catégories",
  "onglet",
  // German
  "seitenleiste",
  "kategorie",
  "kategorien",
  "registerkarte",
  // Hindi
  "साइडबार",
  "श्रेणी",
  "श्रेणियाँ",
  "टैब",
  // Italian
  "barra laterale",
  "categoria",
  "categorie",
  "scheda",
  // Japanese
  "サイドバー",
  "カテゴリー",
  "カテゴリ",
  "タブ",
];

// Category hints to allow noun-only phrases (no verbs) when library is visible
export const LIBRARY_CATEGORY_HINTS = [
  // English
  "template",
  "stock image",
  "images",
  "videos",
  "audios",
  "audio",
  "under review",
  "liked",
  "bookmark",
  "my collection",
  "added from collections",
  "added from collection",
  "work in progress",
  "public spin wheel",
  "public spinwheel",
  "spin wheel",
  "spinwheel",
  "public speed wheel",
  "speed wheel",
  "publish spin wheel",
  // Spanish (basic)
  "plantilla",
  "imagen de stock",
  "imagenes",
  "imágenes",
  "videos",
  "audios",
  "bajo revision",
  "bajo revisión",
  "me gusta",
  "favoritos",
  "mi coleccion",
  "mi colección",
  // Portuguese (basic)
  "modelo",
  "imagem de estoque",
  "imagens",
  "videos",
  "audios",
  "sob revisao",
  "sob revisão",
  "curtidos",
  "favorito",
  "minha colecao",
  "minha coleção",
  // French (basic)
  "modèle",
  "modele",
  "image de stock",
  "images",
  "vidéos",
  "videos",
  "audios",
  "en examen",
  "sous examen",
  "aimés",
  "favoris",
  "ma collection",
  // German (basic)
  "vorlage",
  "lagerbild",
  "stockbild",
  "bilder",
  "videos",
  "audios",
  "unter überprüfung",
  "unter prufung",
  "unter prüfung",
  "favoriten",
  "meine sammlung",
  // Hindi (basic)
  "टेम्पलेट",
  "स्टॉक इमेज",
  "चित्र",
  "छवियाँ",
  "वीडियो",
  "ऑडियो",
  "समीक्षा के तहत",
  "पसंदीदा",
  "मेरी संग्रह",
  // Italian (basic)
  "modello",
  "immagine stock",
  "immagini",
  "video",
  "audios",
  "in revisione",
  "piaciuti",
  "segnalibro",
  "la mia collezione",
  // Japanese (basic)
  "テンプレート",
  "ストック画像",
  "画像",
  "ビデオ",
  "動画",
  "オーディオ",
  "審査中",
  "いいね",
  "ブックマーク",
  "マイコレクション",
];

// Map common phrases, typos, and synonyms to canonical sidebar titles
export const LIBRARY_CATEGORY_ALIASES = {
  "public spin wheel": "Public Spin Wheel",
  "public spinwheel": "Public Spin Wheel",
  "spin wheel": "Public Spin Wheel",
  spinwheel: "Public Spin Wheel",
  "public speed wheel": "Public Spin Wheel",
  "speed wheel": "Public Spin Wheel",
  "publish spin wheel": "Public Spin Wheel",
  "public spin will": "Public Spin Wheel",
  "work in progress": "Work in progress",
  "added from collections": "Added from collections",
  "added from collection": "Added from collections",
};

// Try to extract a target sidebar/tab name and optional top tab context
export const getLibrarySidebarTarget = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  if (!normalized) return null;

  // Canonical titles we accept for switching; avoid passing unknown phrases
  const LIBRARY_CATEGORY_CANONICAL = [
    "Template",
    "Stock Image",
    "Images",
    "Videos",
    "Audios",
    "Under Review",
    "Liked",
    "Bookmark",
    "My Collection",
    "Added from collections",
    "Work in progress",
    "Public Spin Wheel",
    "Brainstorming",
    "Formula",
    "My Pocket",
    "My Personal Category",
    "Learning Resources",
    "Lesson Planing",
  ];
  const isKnown = (q) =>
    LIBRARY_CATEGORY_CANONICAL.includes(q) ||
    Object.values(LIBRARY_CATEGORY_ALIASES || {}).includes(q);

  // Determine top tab if explicitly mentioned; otherwise leave undefined
  const topTab = getLibraryTopTabTarget(text, langCode) || undefined;

  // Prefer exact match of known category hint phrases (e.g., "my collection")
  const hintRe = new RegExp(
    `\\b(${LIBRARY_CATEGORY_HINTS.map((w) => w.replace(/\\s+/g, "\\s+")).join(
      "|"
    )})\\b`,
    "i"
  );
  const hintMatch = hintRe.exec(normalized);
  const toTitle = (s) =>
    s.replace(/\b\w+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
  const applyAlias = (q) => {
    const key = normalizeForMatching(q);
    return LIBRARY_CATEGORY_ALIASES && LIBRARY_CATEGORY_ALIASES[key]
      ? LIBRARY_CATEGORY_ALIASES[key]
      : q;
  };
  if (hintMatch && hintMatch[1]) {
    const query = applyAlias(toTitle(hintMatch[1]));
    if (!isKnown(query)) return null;
    return { query, topTab };
  }

  // Remove known verbs and nouns to isolate a likely tab/category phrase
  const removeAlt = (src, arr) =>
    src.replace(
      new RegExp(
        `\\b(${arr.map((w) => w.replace(/\\s+/g, "\\s+")).join("|")})\\b`,
        "gi"
      ),
      " "
    );
  let working = normalized;
  working = removeAlt(working, SWITCH_VERBS_GENERIC);
  working = removeAlt(working, OPEN_VERBS_GENERIC);
  working = removeAlt(working, LIBRARY_NOUNS_GENERIC);
  working = removeAlt(working, DIALOG_NOUNS_GENERIC);
  working = removeAlt(working, SIDEBAR_NOUNS_GENERIC);

  // Remove common stop words across languages
  const STOP_WORDS = [
    // English
    "the",
    "to",
    "in",
    "on",
    "at",
    "of",
    "for",
    "a",
    "an",
    "please",
    // Spanish
    "el",
    "la",
    "los",
    "las",
    "de",
    "del",
    "al",
    "en",
    "a",
    // Portuguese
    "o",
    "a",
    "os",
    "as",
    "de",
    "do",
    "da",
    "em",
    "para",
    // French
    "le",
    "la",
    "les",
    "de",
    "des",
    "du",
    "à",
    "a",
    "en",
    // German
    "der",
    "die",
    "das",
    "den",
    "dem",
    "des",
    "zu",
    "im",
    "in",
    // Hindi
    "का",
    "की",
    "के",
    "में",
    "पर",
    // Italian
    "il",
    "lo",
    "la",
    "i",
    "gli",
    "le",
    "di",
    "del",
    "della",
    "in",
    "a",
    // Japanese (particles as stop words)
    "の",
    "に",
    "へ",
    "と",
    "で",
    "を",
  ];
  working = removeAlt(working, STOP_WORDS).replace(/\s+/g, " ").trim();

  // Heuristic: take last 1-4 words as the likely target phrase
  const tokens = working.split(/\s+/).filter(Boolean);
  if (!tokens.length) return null;
  const lastPhrase = tokens.slice(Math.max(0, tokens.length - 4)).join(" ");

  const queryCandidate = toTitle(lastPhrase);
  if (!queryCandidate) return null;
  const query = applyAlias(queryCandidate);
  if (!isKnown(query)) return null;

  return { query, topTab };
};

// Determine if the text is intending to switch a library sidebar/category/tab
export const isSwitchLibrarySidebarTabIntent = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  if (!normalized) return false;
  // Allow explicit open/show verbs AND switch/go-to phrasing for sidebar/category switching
  const openVerbRe = buildAltRegex(OPEN_VERBS_GENERIC);
  const switchVerbRe = buildAltRegex(SWITCH_VERBS_GENERIC);
  const hasGoTo = /\b(go\s*to)\b/i.test(normalized);
  const hasVerb =
    openVerbRe.test(normalized) || switchVerbRe.test(normalized) || hasGoTo;

  // Detect sidebar/library nouns or explicit mention of a tab
  const hasLibraryOrSidebar = buildAltRegex([
    ...LIBRARY_NOUNS_GENERIC,
    ...SIDEBAR_NOUNS_GENERIC,
  ]).test(normalized);
  const hasTabWord = /\b(tab|pestaña|aba|onglet|registerkarte)\b/i.test(
    normalized
  );

  const hasTarget = !!getLibrarySidebarTarget(text, langCode);
  const isCategoryOnly = buildAltRegex(LIBRARY_CATEGORY_HINTS).test(normalized);

  // Prioritize precise category-only detection and allow verbs + tab mention
  return (
    (isCategoryOnly && hasTarget) ||
    (hasVerb && (hasLibraryOrSidebar || hasTabWord) && hasTarget)
  );
};

// ===== Template and Export intents (multilingual) =====
// Canvas nouns across languages
export const CANVAS_NOUNS_GENERIC = [
  // English
  "canvas",
  "whiteboard",
  "board",
  // Spanish
  "lienzo",
  // Portuguese
  "tela",
  // French
  "toile",
  // German
  "leinwand",
  // Hindi
  "कैनवास",
  // Italian
  "tela",
  // Japanese
  "キャンバス",
  // Common misspelling
  "canva",
];

// Template nouns across languages
export const TEMPLATE_NOUNS_GENERIC = [
  "template",
  "plantilla",
  "modelo",
  "modèle",
  "modele",
  "vorlage",
  "टेम्पलेट",
  "modello",
  "テンプレート",
];

// Collection nouns
export const COLLECTION_NOUNS_GENERIC = [
  "collection",
  "collections",
  "coleccion",
  "colecciones",
  "colección",
  "colecao",
  "coleções",
  "collection",
  "collections",
  "sammlung",
  "sammlungen",
  "संग्रह",
  "collezione",
  "collezioni",
  "コレクション",
];

// Verbs to use/apply a template
const TEMPLATE_VERBS_GENERIC = [
  // English
  "use",
  "apply",
  "insert",
  "place",
  "put",
  "add",
  // Spanish
  "usar",
  "aplicar",
  "insertar",
  "colocar",
  "poner",
  "añadir",
  // Portuguese
  "usar",
  "aplicar",
  "inserir",
  "colocar",
  "adicionar",
  // French
  "utiliser",
  "appliquer",
  "insérer",
  // German
  "verwenden",
  "anwenden",
  "einfügen",
  // Hindi
  "उपयोग",
  "लागू",
  "डालें",
  // Italian
  "usa",
  "applica",
  "inserisci",
  // Japanese
  "使う",
  "適用",
  "挿入",
];

// Verbs that imply export/place on canvas
const EXPORT_VERBS_GENERIC = [
  // English
  "export",
  "place",
  "put",
  "add",
  "insert",
  "apply",
  "use",
  // Spanish
  "exportar",
  // Portuguese
  "exportar",
  // French
  "exporter",
  // German
  "exportieren",
  // Hindi
  "निर्यात",
  // Italian
  "esportare",
  // Japanese
  "エクスポート",
];

// Extract template name for "use/apply/insert template <name>" or "<name> template"
export const getUseTemplateTarget = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return null;
  const verbsAlt = TEMPLATE_VERBS_GENERIC.map(spacey).join("|");
  const nameAfterTemplate = new RegExp(
    `(?:${verbsAlt})\\s+(?:the\\s+)?(?:template)\\s+(?:called|named)?\\s*(?:\"([^\"]+)\"|'([^']+)'|([\\p{L}\\p{N}\\s]{2,}))`,
    "i",
  );
  let m = nameAfterTemplate.exec(s);
  if (m) return (m[1] || m[2] || m[3] || "").trim();
  // Fallback: "apply <name> template"
  const nameBeforeTemplate = new RegExp(
    `(?:${verbsAlt})\\s+(?:\"([^\"]+)\"|'([^']+)'|([\\p{L}\\p{N}\\s]{2,}))\\s+(?:template)`,
    "i",
  );
  m = nameBeforeTemplate.exec(s);
  if (m) return (m[1] || m[2] || m[3] || "").trim();
  return null;
};

export const isUseTemplateIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(TEMPLATE_VERBS_GENERIC).test(s);
  const hasNoun = buildAltRegex(TEMPLATE_NOUNS_GENERIC).test(s);
  return hasVerb && hasNoun;
};

export const getExportTarget = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return null;
  const verbsAlt = EXPORT_VERBS_GENERIC.map(spacey).join("|");
  // Capture both the name and the noun to preserve titles like "New Collection"
  const re = new RegExp(
    `(?:${verbsAlt})\\s+(?:the\\s+)?(.+?)\\s+(library|template|collection)(?:\\s+(?:to|onto|on|into)\\s+(?:the\\s+)?)?`,
    "i",
  );
  const m = re.exec(s);
  if (m) {
    const raw = (m[1] || "").trim();
    const noun = (m[2] || "").trim().toLowerCase();
    const rawLower = raw.toLowerCase();
    const rawHasNoun = /(library|template|collection)\b/.test(rawLower);

    // For numbered collections: "collection 5" or "5" with noun "collection"
    const isNumberedCollection = noun === "collection" && /^\d+$/.test(raw);
    const isRawCollectionNumbered = rawLower.startsWith("collection ") && /^\d+$/.test(rawLower.replace(/^collection\s+/, ""));

    let name;
    if (isNumberedCollection) {
      // "Collection 5" format when only number captured
      name = `${noun.charAt(0).toUpperCase() + noun.slice(1)} ${raw}`;
    } else if (isRawCollectionNumbered) {
      // Already in "collection N" form; don't append other nouns
      name = raw;
    } else if (rawHasNoun) {
      // If the raw segment already includes a noun, treat it as the full title
      name = raw;
    } else if (noun && noun !== "library" && !rawLower.endsWith(noun)) {
      // "New Collection" format for descriptive names
      name = `${raw} ${noun}`;
    } else {
      name = raw;
    }
    return { templateName: name };
  }
  return null;
};

export const isExportToCanvasIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(EXPORT_VERBS_GENERIC).test(s);
  const hasCanvas = buildAltRegex(CANVAS_NOUNS_GENERIC).test(s) || /\bcanva\w*\b/i.test(s);
  const hasNoun = buildAltRegex([
    ...TEMPLATE_NOUNS_GENERIC,
    ...LIBRARY_NOUNS_GENERIC,
    ...COLLECTION_NOUNS_GENERIC,
  ]).test(s);
  return hasVerb && hasNoun && hasCanvas;
};

export const isExportSelectedCollectionIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  if (!isExportToCanvasIntent(text, langCode)) return false;
  const name = getUseTemplateTarget(text, langCode) || (getExportTarget(text, langCode)?.templateName);
  const hasSelected = /\b(selected|current|marked|chosen)\b/i.test(s);
  const hasCollectionWord = buildAltRegex(COLLECTION_NOUNS_GENERIC).test(s);
  return !name && (hasSelected || hasCollectionWord);
};

// New Page intent detection
export const PAGE_NOUNS_GENERIC = [
  "page",
  "pages",
  "pagina",
  "página",
  "paginas",
  "páginas",
  "seite",
  "seiten",
  "पृष्ठ",
  "pagine",
  "pagina",
  "ページ",
];

export const PAGE_VERBS_GENERIC = [
  "add",
  "create",
  "make",
  "new",
  "insert",
  "agregar",
  "añadir",
  "crear",
  "hacer",
  "nueva",
  "nuevo",
  "adicionar",
  "criar",
  "fazer",
  "nova",
  "novo",
  "ajouter",
  "creer",
  "créer",
  "faire",
  "nouvelle",
  "nouveau",
  "hinzufugen",
  "hinzufügen",
  "erstellen",
  "machen",
  "neu",
  "neue",
  "neuen",
  "जोड़ें",
  "बनाएं",
  "नया",
  "नई",
  "aggiungi",
  "crea",
  "fare",
  "nuova",
  "nuovo",
  "追加",
  "作成",
  "新しい",
];

const PAGE_SYNONYMS = {
  en: {
    add: ["new page", "add page", "add a page", "create page", "create a page"],
  },
  es: {
    add: [
      "nueva pagina",
      "nueva página",
      "agregar pagina",
      "agregar página",
      "añadir pagina",
      "añadir página",
      "crear pagina",
      "crear página",
    ],
  },
  pt: {
    add: [
      "nova pagina",
      "nova página",
      "adicionar pagina",
      "adicionar página",
      "criar pagina",
      "criar página",
    ],
  },
  fr: {
    add: ["nouvelle page", "ajouter une page", "creer une page", "créer une page"],
  },
  de: {
    add: ["neue seite", "seite hinzufügen", "seite erstellen"],
  },
  hi: {
    add: ["नई पृष्ठ", "नया पृष्ठ", "पृष्ठ जोड़ें", "पृष्ठ बनाएं"],
  },
  it: {
    add: ["nuova pagina", "aggiungi una pagina", "crea una pagina"],
  },
  ja: {
    add: ["新しいページ", "ページを追加", "ページを作成"],
  },
};

export const isAddNewPageIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const code = (langCode || "en").slice(0, 2);
  const syn = PAGE_SYNONYMS[code]?.add || [];
  if (syn.length && buildAltRegex(syn).test(s)) return true;
  return buildAltRegex(PAGE_VERBS_GENERIC).test(s) && buildAltRegex(PAGE_NOUNS_GENERIC).test(s);
};

// Extract target page name for switching by name across languages.
// Examples: "switch to page Introduction", "go to pagina Introducción".
export const getSwitchPageNameTarget = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  if (!normalized) return null;

  const verbRe = new RegExp(`\\b(${["switch", "go", "go to", "cambiar", "mudar", "changer", "wechseln", "स्विच", "passa a", "切り替え"].map((w) => w.replace(/\\s+/g, "\\s+")).join("|")})\\b`, "i");
  const nounRe = buildAltRegex(PAGE_NOUNS_GENERIC);

  let candidate = null;
  // Pattern: <verb> [to] [the] <page-noun> <name>
  const re1 = new RegExp(`${verbRe.source}\\s+(?:the\\s+)?${nounRe.source}\\s+(.+)`, "i");
  let m = normalized.match(re1);
  if (m && m.length >= 1) {
    candidate = m[m.length - 1];
  }

  // Pattern: <verb> <name> <page-noun>
  if (!candidate) {
    const re2 = new RegExp(`${verbRe.source}\\s+(.+?)\\s+(?:the\\s+)?${nounRe.source}\\b`, "i");
    m = normalized.match(re2);
    if (m && m.length >= 1) {
      candidate = m[m.length - 1];
    }
  }

  // No fallback without an explicit page noun to avoid misrouting

  if (!candidate) return null;

  const name = candidate
    .replace(/^["“”'‘’]+|["“”'‘’]+$/g, "")
    .replace(/\s+(please|now)$/i, "")
    .trim();
  if (!name) return null;
  // Filter common filler/connector phrases that can be captured between verb and noun
  const STOP_WORDS = [
    "go",
    "go to",
    "to",
    "the",
    "a",
    "an",
    // Spanish
    "ir",
    "ir a",
    "a",
    "la",
    "el",
    // Portuguese/Italian/French/German basics
    "vai",
    "vai a",
    "le",
    "la",
    "der",
    "die",
    "das",
  ];
  if (STOP_WORDS.includes(name.toLowerCase())) return null;
  // Ignore purely numeric targets (handled by page-number switching)
  if (/^\d+$/.test(name)) return null;
  // Require some alphabetic characters to avoid capturing only connectors
  if (!/[a-zA-Z\p{L}]/u.test(name)) return null;
  return name;
};

// Determine if text intends to switch page by dynamic name.
export const isSwitchPageByNameIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(["switch", "go", "go to", "cambiar", "mudar", "changer", "wechseln", "स्विच", "passa a", "切り替え"]).test(s) || /\b(go\s*to)\b/i.test(s);
  const hasPageNoun = buildAltRegex(PAGE_NOUNS_GENERIC).test(s);
  const target = getSwitchPageNameTarget(text, langCode);
  return hasVerb && hasPageNoun && !!target;
};

// Copy/Delete page by name intents
const COPY_VERBS_GENERIC = [
  // English
  "copy",
  "duplicate",
  "clone",
  // Spanish
  "copiar",
  "duplicar",
  "clonar",
  // Portuguese
  "copiar",
  "duplicar",
  "clonar",
  // French
  "copier",
  "dupliquer",
  "cloner",
  // German
  "kopieren",
  "duplizieren",
  "klonen",
  // Hindi
  "कॉपी",
  "प्रतिलिपि",
  "नकल",
  // Italian
  "copia",
  "copiare",
  "duplicare",
  // Japanese
  "コピー",
  "複製",
  "クローン",
];

const DELETE_VERBS_GENERIC = [
  // English
  "delete",
  "remove",
  "erase",
  "trash",
  "discard",
  // Spanish
  "eliminar",
  "borrar",
  "suprimir",
  // Portuguese
  "excluir",
  "deletar",
  "apagar",
  // French
  "supprimer",
  "effacer",
  // German
  "löschen",
  "loeschen",
  "entfernen",
  // Hindi
  "हटाएं",
  "हटाना",
  "मिटाएं",
  // Italian
  "eliminare",
  "cancellare",
  // Japanese
  "削除",
  "消去",
];

export const getCopyPageNameTarget = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  if (!normalized) return null;
  const verbRe = buildAltRegex(COPY_VERBS_GENERIC);
  const nounRe = buildAltRegex(PAGE_NOUNS_GENERIC);

  let candidate = null;
  // Pattern: <verb> [the] <page-noun> <name>
  const re1 = new RegExp(`${verbRe.source}\\s+(?:the\\s+)?${nounRe.source}\\s+(.+)`, "i");
  let m = normalized.match(re1);
  if (m && m.length >= 1) {
    candidate = m[m.length - 1];
  }

  // Pattern: <verb> <name> <page-noun>
  if (!candidate) {
    const re2 = new RegExp(`${verbRe.source}\\s+(.+?)\\s+(?:the\\s+)?${nounRe.source}\\b`, "i");
    m = normalized.match(re2);
    if (m && m.length >= 1) {
      candidate = m[m.length - 1];
    }
  }

  if (!candidate) return null;
  const name = candidate
    .replace(/^["“”'‘’]+|["“”'‘’]+$/g, "")
    .replace(/\s+(please|now)$/i, "")
    .trim();
  if (!name) return null;
  if (/^\d+$/.test(name)) return null;
  if (!/[a-zA-Z\p{L}]/u.test(name)) return null;
  return name;
};

export const isCopyPageByNameIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(COPY_VERBS_GENERIC).test(s);
  const hasPageNoun = buildAltRegex(PAGE_NOUNS_GENERIC).test(s);
  const target = getCopyPageNameTarget(text, langCode);
  return hasVerb && hasPageNoun && !!target;
};

// Detect intent to copy a page by explicit number, e.g., "copy page 1"
export const isCopyPageByNumberIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(COPY_VERBS_GENERIC).test(s);
  const hasPageNoun = buildAltRegex(PAGE_NOUNS_GENERIC).test(s);
  const num = getSwitchPageNumberTarget(text, langCode);
  return hasVerb && hasPageNoun && Number.isInteger(num);
};

export const getDeletePageNameTarget = (text, langCode) => {
  const normalized = normalizeForMatching(text);
  if (!normalized) return null;
  const verbRe = buildAltRegex(DELETE_VERBS_GENERIC);
  const nounRe = buildAltRegex(PAGE_NOUNS_GENERIC);

  let candidate = null;
  // Pattern: <verb> [the] <page-noun> <name>
  const re1 = new RegExp(`${verbRe.source}\\s+(?:the\\s+)?${nounRe.source}\\s+(.+)`, "i");
  let m = normalized.match(re1);
  if (m && m.length >= 1) {
    candidate = m[m.length - 1];
  }

  // Pattern: <verb> <name> <page-noun>
  if (!candidate) {
    const re2 = new RegExp(`${verbRe.source}\\s+(.+?)\\s+(?:the\\s+)?${nounRe.source}\\b`, "i");
    m = normalized.match(re2);
    if (m && m.length >= 1) {
      candidate = m[m.length - 1];
    }
  }

  if (!candidate) return null;
  const name = candidate
    .replace(/^["“”'‘’]+|["“”'‘’]+$/g, "")
    .replace(/\s+(please|now)$/i, "")
    .trim();
  if (!name) return null;
  if (/^\d+$/.test(name)) return null;
  if (!/[a-zA-Z\p{L}]/u.test(name)) return null;
  return name;
};

export const isDeletePageByNameIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(DELETE_VERBS_GENERIC).test(s);
  const hasPageNoun = buildAltRegex(PAGE_NOUNS_GENERIC).test(s);
  const target = getDeletePageNameTarget(text, langCode);
  return hasVerb && hasPageNoun && !!target;
};

// Detect intent to delete a page by explicit number, e.g., "delete page 2"
export const isDeletePageByNumberIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(DELETE_VERBS_GENERIC).test(s);
  const hasPageNoun = buildAltRegex(PAGE_NOUNS_GENERIC).test(s);
  const num = getSwitchPageNumberTarget(text, langCode);
  return hasVerb && hasPageNoun && Number.isInteger(num);
};

// Extract target page number for switching by number across languages.
// Examples: "go to the page 3", "switch to page 2".
export const getSwitchPageNumberTarget = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return null;

  const nounRe = buildAltRegex(PAGE_NOUNS_GENERIC);
  const verbRe = buildAltRegex(["switch", "go", "go to", "cambiar", "mudar", "changer", "wechseln", "स्विच", "passa a", "切り替え"]);

  // Support spelled-out numbers in multiple languages (basic range)
  const NUMBER_WORDS_MULTI = {
    en: {
      zero: 0,
      one: 1,
      two: 2,
      three: 3,
      four: 4,
      five: 5,
      six: 6,
      seven: 7,
      eight: 8,
      nine: 9,
      ten: 10,
      eleven: 11,
      twelve: 12,
      thirteen: 13,
      fourteen: 14,
      fifteen: 15,
      sixteen: 16,
      seventeen: 17,
      eighteen: 18,
      nineteen: 19,
      twenty: 20,
      thirty: 30,
      forty: 40,
      fifty: 50,
      sixty: 60,
      seventy: 70,
      eighty: 80,
      ninety: 90,
    },
    es: {
      cero: 0,
      uno: 1,
      una: 1,
      dos: 2,
      tres: 3,
      cuatro: 4,
      cinco: 5,
      seis: 6,
      siete: 7,
      ocho: 8,
      nueve: 9,
      diez: 10,
      once: 11,
      doce: 12,
      trece: 13,
      catorce: 14,
      quince: 15,
      dieciseis: 16,
      dieciséis: 16,
      diecisiete: 17,
      dieciocho: 18,
      diecinueve: 19,
      veinte: 20,
    },
    pt: {
      zero: 0,
      um: 1,
      uma: 1,
      dois: 2,
      duas: 2,
      tres: 3,
      três: 3,
      quatro: 4,
      cinco: 5,
      seis: 6,
      sete: 7,
      oito: 8,
      nove: 9,
      dez: 10,
      onze: 11,
      doze: 12,
      treze: 13,
      quatorze: 14,
      catorze: 14,
      quinze: 15,
      dezesseis: 16,
      dezassete: 17,
      dezoito: 18,
      dezenove: 19,
      vinte: 20,
    },
    fr: {
      zero: 0,
      zéro: 0,
      un: 1,
      une: 1,
      deux: 2,
      trois: 3,
      quatre: 4,
      cinq: 5,
      six: 6,
      sept: 7,
      huit: 8,
      neuf: 9,
      dix: 10,
    },
    de: {
      null: 0,
      eins: 1,
      zwei: 2,
      drei: 3,
      vier: 4,
      fünf: 5,
      funf: 5,
      sechs: 6,
      sieben: 7,
      acht: 8,
      neun: 9,
      zehn: 10,
    },
    it: {
      zero: 0,
      uno: 1,
      una: 1,
      due: 2,
      tre: 3,
      quattro: 4,
      cinque: 5,
      sei: 6,
      sette: 7,
      otto: 8,
      nove: 9,
      dieci: 10,
    },
    hi: {
      शून्य: 0,
      एक: 1,
      दो: 2,
      तीन: 3,
      चार: 4,
      पाँच: 5,
      पांच: 5,
      छह: 6,
      सात: 7,
      आठ: 8,
      नौ: 9,
      दस: 10,
    },
    ja: {
      零: 0,
      〇: 0,
      一: 1,
      二: 2,
      三: 3,
      四: 4,
      五: 5,
      六: 6,
      七: 7,
      八: 8,
      九: 9,
      十: 10,
    },
  };

  const parseNumberWordMulti = (w, lang) => {
    const t = String(w || "").trim().toLowerCase();
    if (!t) return null;
    const code = (lang || "en").slice(0, 2);
    const dict = NUMBER_WORDS_MULTI[code] || NUMBER_WORDS_MULTI.en;
    if (Object.prototype.hasOwnProperty.call(dict, t)) return dict[t];
    // English composite tens + units (e.g., "twenty one")
    if (code === "en") {
      const parts = t.split(/\s+/);
      if (parts.length === 2) {
        const a = dict[parts[0]];
        const b = dict[parts[1]];
        if (Number.isInteger(a) && Number.isInteger(b) && a >= 20 && a % 10 === 0 && b > 0) {
          return a + b;
        }
      }
    }
    return null;
  };

  // Pattern: <verb> [to] [the] <page-noun> <number>
  let m = s.match(new RegExp(`${verbRe.source}\\s+(?:the\\s+)?${nounRe.source}\\s+(\\d+)`));
  if (m && m.length >= 1) {
    const numStr = m[m.length - 1]; // last capture is the number
    if (numStr) return parseInt(numStr, 10);
  }

  // Pattern: <page-noun> <number>
  m = s.match(new RegExp(`${nounRe.source}\\s+(\\d+)`));
  if (m && m.length >= 1) {
    const numStr = m[m.length - 1];
    if (numStr) return parseInt(numStr, 10);
  }

  // Word-number: <verb> [to] [the] <page-noun> <word-number>
  m = s.match(new RegExp(`${verbRe.source}\\s+(?:the\\s+)?${nounRe.source}\\s+([\\p{L}]+(?:\\s+[\\p{L}]+)?)$`, "iu"));
  if (m && m.length >= 1) {
    const word = m[m.length - 1];
    const num = parseNumberWordMulti(word, langCode);
    if (Number.isInteger(num)) return num;
  }

  // Word-number: <page-noun> <word-number>
  m = s.match(new RegExp(`${nounRe.source}\\s+([\\p{L}]+(?:\\s+[\\p{L}]+)?)$`, "iu"));
  if (m && m.length >= 1) {
    const word = m[m.length - 1];
    const num = parseNumberWordMulti(word, langCode);
    if (Number.isInteger(num)) return num;
  }

  // Fallback: any trailing number after verb phrases
  m = s.match(/\b(go\s*to|switch)\b[^\d]*(\d+)/);
  if (m && m[2]) return parseInt(m[2], 10);

  return null;
};

// Determine if text intends to switch page by explicit number.
export const isSwitchPageByNumberIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(["switch", "go", "go to", "cambiar", "mudar", "changer", "wechseln", "स्विच", "passa a", "切り替え"]).test(s) || /\b(go\s*to)\b/i.test(s);
  const hasPageNoun = buildAltRegex(PAGE_NOUNS_GENERIC).test(s);
  const num = getSwitchPageNumberTarget(text, langCode);
  return hasVerb && hasPageNoun && Number.isInteger(num);
};

// Background change intent detection (multilingual)
// Nouns referring to the canvas background across languages
export const BACKGROUND_NOUNS_GENERIC = [
  // English
  "background",
  "bg",
  // Spanish
  "fondo",
  // Portuguese
  "fundo",
  // French
  "arriere plan",
  "arrière plan",
  "arriere-plan",
  "arrière-plan",
  "fond",
  // German
  "hintergrund",
  // Hindi
  "पृष्ठभूमि",
  // Italian
  "sfondo",
  // Japanese
  "背景",
];

// Verbs that imply changing or setting properties
export const CHANGE_VERBS_GENERIC = [
  // English
  "set",
  "change",
  "make",
  "update",
  "apply",
  // Spanish
  "establecer",
  "cambiar",
  "poner",
  "hacer",
  // Portuguese
  "definir",
  "mudar",
  "alterar",
  "aplicar",
  // French
  "mettre",
  "changer",
  "rendre",
  "appliquer",
  // German
  "setzen",
  "ändern",
  "machen",
  "anwenden",
  // Hindi (basic verbs)
  "सेट",
  "बदलें",
  "बदलना",
  "बनाएं",
  // Italian
  "imposta",
  "cambiare",
  "cambia",
  "applica",
  // Japanese
  "設定",
  "変更",
  "変えて",
  "適用",
];

// Words indicating transparency/no background
export const TRANSPARENT_WORDS_GENERIC = [
  // English
  "transparent",
  "transparency",
  "no background",
  "clear",
  // Spanish
  "transparente",
  "sin fondo",
  "fondo transparente",
  // Portuguese
  "transparente",
  "sem fundo",
  "fundo transparente",
  // French
  "transparent",
  "sans fond",
  "fond transparent",
  // German
  "transparent",
  "ohne hintergrund",
  "durchsichtig",
  // Hindi
  "पारदर्शी",
  "बिना पृष्ठभूमि",
  // Italian
  "trasparente",
  "senza sfondo",
  // Japanese
  "透明",
  "背景なし",
];

// Map common color words across languages to CSS color names
const COLOR_DICT = {
  en: {
    red: "red",
    blue: "blue",
    green: "green",
    black: "black",
    white: "white",
    yellow: "yellow",
    orange: "orange",
    pink: "pink",
    purple: "purple",
    violet: "purple",
    gray: "gray",
    grey: "gray",
    brown: "brown",
    cyan: "cyan",
    magenta: "magenta",
  },
  es: {
    rojo: "red",
    azul: "blue",
    verde: "green",
    negro: "black",
    blanco: "white",
    amarillo: "yellow",
    naranja: "orange",
    rosa: "pink",
    morado: "purple",
    violeta: "purple",
    gris: "gray",
    marron: "brown",
    café: "brown",
    cafe: "brown",
    cian: "cyan",
    magenta: "magenta",
  },
  pt: {
    vermelho: "red",
    azul: "blue",
    verde: "green",
    preto: "black",
    branco: "white",
    amarelo: "yellow",
    laranja: "orange",
    rosa: "pink",
    roxo: "purple",
    violeta: "purple",
    cinza: "gray",
    castanho: "brown",
    marrom: "brown",
    ciano: "cyan",
    magenta: "magenta",
  },
  fr: {
    rouge: "red",
    bleu: "blue",
    vert: "green",
    noir: "black",
    blanc: "white",
    jaune: "yellow",
    orange: "orange",
    rose: "pink",
    violet: "purple",
    pourpre: "purple",
    gris: "gray",
    marron: "brown",
    cyan: "cyan",
    magenta: "magenta",
  },
  de: {
    rot: "red",
    blau: "blue",
    grün: "green",
    gruen: "green",
    schwarz: "black",
    weiß: "white",
    weiss: "white",
    gelb: "yellow",
    orange: "orange",
    rosa: "pink",
    lila: "purple",
    violett: "purple",
    grau: "gray",
    braun: "brown",
    cyan: "cyan",
    magenta: "magenta",
  },
  hi: {
    लाल: "red",
    नीला: "blue",
    हरा: "green",
    काला: "black",
    सफेद: "white",
    पीला: "yellow",
    नारंगी: "orange",
    गुलाबी: "pink",
    बैंगनी: "purple",
    जामुनी: "purple",
    धूसर: "gray",
    भूरा: "brown",
  },
  it: {
    rosso: "red",
    blu: "blue",
    verde: "green",
    nero: "black",
    bianco: "white",
    giallo: "yellow",
    arancione: "orange",
    rosa: "pink",
    viola: "purple",
    porpora: "purple",
    grigio: "gray",
    marrone: "brown",
    ciano: "cyan",
    magenta: "magenta",
  },
  ja: {
    赤: "red",
    青: "blue",
    緑: "green",
    黒: "black",
    白: "white",
    黄色: "yellow",
    オレンジ: "orange",
    ピンク: "pink",
    紫: "purple",
    グレー: "gray",
    茶色: "brown",
    シアン: "cyan",
    マゼンタ: "magenta",
  },
};

const lookupColorWord = (s, langCode) => {
  const dict = COLOR_DICT[getLanguageCode(langCode)] || COLOR_DICT.en;
  for (const [word, color] of Object.entries(dict)) {
    const re = new RegExp(`\\b${spacey(word)}\\b`, "i");
    if (re.test(s)) return color;
  }
  return null;
};

// Extract background target: color hex/name or transparency
export const getBackgroundChangeTarget = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return null;
  // must mention background noun to avoid misrouting
  if (!buildAltRegex(BACKGROUND_NOUNS_GENERIC).test(s)) return null;
  // hex color detection on original text to keep case
  const hexMatch = /#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/.exec(String(text || ""));
  if (hexMatch) {
    return { type: "color", value: `#${hexMatch[1].toLowerCase()}` };
  }
  // transparent intent
  if (buildAltRegex(TRANSPARENT_WORDS_GENERIC).test(s)) {
    return { type: "transparent" };
  }
  // color word
  const colorName = lookupColorWord(s, langCode);
  if (colorName) {
    return { type: "color", value: colorName };
  }
  return null;
};

// Determine if the utterance intends to change background
export const isChangeBackgroundIntent = (text, langCode) => {
  const s = normalizeForMatching(text);
  if (!s) return false;
  const hasVerb = buildAltRegex(CHANGE_VERBS_GENERIC).test(s) || /\b(set\s+to|change\s+to|make\s+it)\b/i.test(s);
  const hasNoun = buildAltRegex(BACKGROUND_NOUNS_GENERIC).test(s);
  const tgt = getBackgroundChangeTarget(text, langCode);
  // Allow noun-only with explicit transparent or color hex/word
  return (hasVerb && hasNoun && !!tgt) || (!!tgt && hasNoun);
};
